/**
 * child.js — S2 global chrome behaviors (wp-site-pipeline behavior map,
 * child.js-owned rows only). Vanilla JS, no jQuery, no framework.
 *
 * Owns:
 *  - announcement bar rotation (5200ms)
 *  - sticky-header .is-scrolled state (announcement collapses)
 *  - mega menus (hover/focus/click-open, 260ms delayed close, Bridal subs)
 *  - mobile hamburger nav (aria-expanded, scroll lock, focus trap, accordion)
 *  - search overlay (toggle + Store API live results, 250ms debounce)
 *  - favourites drawer (localStorage, badge, [data-wynn-fav] delegation,
 *    CustomEvent('wynn:fav') API for S3 blocks)
 *  - bag icon -> WooCommerce mini-cart delegation + badge sync + toast
 *  - member / size modals
 *  - footer locale selectors (language + ship-to, persistence, mobile sync)
 *  - newsletter form -> toast
 *
 * NOT owned here (S3): per-block rail scroll-snap, block-level overlays.
 */

(() => {
	'use strict';

	const $  = (sel, ctx = document) => ctx.querySelector(sel);
	const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

	const FAVS_KEY = 'wynn-favs';
	const LANG_KEY = 'wynn-lang';
	const CUR_KEY  = 'wynn-cur';
	const CUR_DOT_KEY = 'wynn-cur-dot';

	const wideMQ = window.matchMedia('(min-width: 1024px)');

	// =========================================================================
	// Toast
	// =========================================================================
	const toastEl = $('[data-wynn-toast]');
	let toastTimer = null;

	function toast(msg) {
		if (!toastEl) return;
		toastEl.textContent = msg;
		toastEl.hidden = false;
		clearTimeout(toastTimer);
		toastTimer = setTimeout(() => { toastEl.hidden = true; }, 2200);
	}

	// =========================================================================
	// Overlay manager — scroll lock, focus trap, Escape, focus restore
	// =========================================================================
	const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
	let openOverlays = [];

	function lockScroll(on) {
		document.body.classList.toggle('wynn-lock', on);
	}

	function trapFocus(overlay, evt) {
		if (evt.key !== 'Tab') return;
		const nodes = $$(FOCUSABLE, overlay).filter((el) => el.offsetParent !== null);
		if (!nodes.length) return;
		const first = nodes[0];
		const last = nodes[nodes.length - 1];
		if (evt.shiftKey && document.activeElement === first) {
			evt.preventDefault();
			last.focus();
		} else if (!evt.shiftKey && document.activeElement === last) {
			evt.preventDefault();
			first.focus();
		}
	}

	function openOverlay(key, focusSel) {
		const overlay = $(`[data-wynn-overlay="${key}"]`);
		if (!overlay || openOverlays.includes(key)) return;
		const restore = document.activeElement;
		overlay.hidden = false;
		openOverlays.push(key);
		lockScroll(true);
		overlay._trap = (e) => trapFocus(overlay, e);
		overlay.addEventListener('keydown', overlay._trap);
		const target = focusSel ? $(focusSel, overlay) : $(FOCUSABLE, overlay);
		if (target) requestAnimationFrame(() => target.focus());
		syncTriggers(key, true);
		overlay._restore = restore;
	}

	function closeOverlay(key) {
		const overlay = $(`[data-wynn-overlay="${key}"]`);
		if (!overlay) return;
		openOverlays = openOverlays.filter((k) => k !== key);
		overlay.hidden = true;
		overlay.removeEventListener('keydown', overlay._trap);
		syncTriggers(key, false);
		if (!openOverlays.length) lockScroll(false);
		if (overlay._restore && overlay._restore.focus) overlay._restore.focus();
	}

	function closeAllOverlays() {
		openOverlays.slice().forEach(closeOverlay);
	}

	function syncTriggers(key, expanded) {
		$$(`[data-wynn-open="${key}"]`).forEach((btn) => {
			if (btn.hasAttribute('aria-expanded')) btn.setAttribute('aria-expanded', String(expanded));
		});
		if (key === 'mobile-nav') {
			const burger = $('[data-wynn-mobile-open]');
			if (burger) burger.setAttribute('aria-expanded', String(expanded));
		}
	}

	// Escape closes the top-most overlay.
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && openOverlays.length) {
			closeOverlay(openOverlays[openOverlays.length - 1]);
		}
	});

	// Generic openers / closers (search, favs).
	document.addEventListener('click', (e) => {
		const opener = e.target.closest('[data-wynn-open]');
		if (opener) {
			e.preventDefault();
			openOverlay(opener.dataset.wynnOpen);
			return;
		}
		const closer = e.target.closest('[data-wynn-close]');
		if (closer) {
			const overlay = closer.closest('[data-wynn-overlay]');
			if (overlay) closeOverlay(overlay.dataset.wynnOverlay);
		}
	});

	// =========================================================================
	// Announcement bar — 3 rotating messages, 5200ms (mockup _a interval)
	// =========================================================================
	const annMsgs = $$('.wynn-announcement__msg');
	if (annMsgs.length > 1) {
		let annIdx = 0;
		setInterval(() => {
			annMsgs[annIdx].classList.remove('is-active');
			annIdx = (annIdx + 1) % annMsgs.length;
			annMsgs[annIdx].classList.add('is-active');
		}, 5200);
	}

	// =========================================================================
	// Sticky header state — announcement collapses once the page scrolls
	// =========================================================================
	// Hysteresis: collapsing the bar removes up to ~40px from the sticky header,
	// and scroll anchoring shifts scrollY by the same amount. A single threshold
	// (the old `scrollY > 8`) therefore flip-flopped forever near the top of the
	// page. The gap between the two thresholds must exceed the bar height.
	const headerPart = $('header.wp-block-template-part');

	// Transparent overlay header (client review 2026-10-05, p13): on a page
	// whose hero runs full-bleed under the bar, the header paints transparent
	// with white marks while the page sits at the top — and turns solid as soon
	// as it is scrolled, hovered, or a mega menu is open.
	// Only an image section that opens the page counts: fullHeight is the hero
	// default now, so one placed further down a page must not make the bar
	// transparent over ordinary page background. The summary pages open on a
	// banner stack, which gets the same treatment (as on asia.apm.mc).
	const overlayHero = headerPart ? $('.wynn-hero--overlay, .wynn-card-grid--banner-stack') : null;
	const overlayHeader = !!overlayHero
		&& overlayHero.getBoundingClientRect().top + window.scrollY < 200;
	let headerCollapsed = false;
	let headerHovered = false;
	let headerMegaOpen = false;

	function syncHeaderTransparency() {
		if (!overlayHeader) return;
		const solid = headerCollapsed || headerHovered || headerMegaOpen;
		headerPart.classList.toggle('is-transparent', !solid);
	}

	if (headerPart) {
		const COLLAPSE_AT = 60;
		const EXPAND_AT = 8;
		let ticking = false;
		const update = () => {
			ticking = false;
			const y = window.scrollY;
			if (!headerCollapsed && y > COLLAPSE_AT) {
				headerCollapsed = true;
			} else if (headerCollapsed && y < EXPAND_AT) {
				headerCollapsed = false;
			} else {
				return;
			}
			headerPart.classList.toggle('is-scrolled', headerCollapsed);
			syncHeaderTransparency();
		};
		const onScroll = () => {
			if (!ticking) {
				ticking = true;
				requestAnimationFrame(update);
			}
		};
		window.addEventListener('scroll', onScroll, { passive: true });
		update();
	}

	if (overlayHeader) {
		document.body.classList.add('wynn-has-overlay-header');

		// The full-bleed hero sits below the announcement bar and under the
		// header bar, so its height is the viewport minus the announcement.
		// Measure it rather than hard-coding: the bar is 34px on desktop and
		// 40px on mobile, and a longer message can change that again.
		const announcement = $('.wynn-announcement');
		const syncAnnouncementHeight = () => {
			if (!announcement || headerCollapsed) return; // collapsed = height 0
			const h = announcement.offsetHeight;
			if (h) document.documentElement.style.setProperty('--wynn-announce-height', h + 'px');
		};
		syncAnnouncementHeight();
		window.addEventListener('resize', syncAnnouncementHeight, { passive: true });

		headerPart.addEventListener('mouseenter', () => { headerHovered = true; syncHeaderTransparency(); });
		headerPart.addEventListener('mouseleave', () => { headerHovered = false; syncHeaderTransparency(); });
		headerPart.addEventListener('focusin', () => { headerHovered = true; syncHeaderTransparency(); });
		headerPart.addEventListener('focusout', () => { headerHovered = false; syncHeaderTransparency(); });
		syncHeaderTransparency();
	}

	// =========================================================================
	// Mega menus (desktop >= 1024px) — hover/focus open, 260ms delayed close,
	// first click opens the panel, second click navigates (mockup navItems.go)
	// =========================================================================
	let megaTimer = null;
	const megaTriggers = $$('[data-wynn-mega-trigger]');

	function openMega(key) {
		clearTimeout(megaTimer);
		$$('.wynn-mega').forEach((panel) => {
			panel.hidden = panel.dataset.wynnMega !== key;
		});
		megaTriggers.forEach((t) => {
			t.classList.toggle('is-mega-active', t.dataset.wynnMegaTrigger === key);
		});
		headerMegaOpen = true;
		syncHeaderTransparency();
	}

	function closeMegaSoon() {
		clearTimeout(megaTimer);
		megaTimer = setTimeout(closeMega, 260);
	}

	function closeMega() {
		clearTimeout(megaTimer);
		$$('.wynn-mega').forEach((panel) => { panel.hidden = true; });
		megaTriggers.forEach((t) => t.classList.remove('is-mega-active'));
		headerMegaOpen = false;
		syncHeaderTransparency();
	}

	if (megaTriggers.length) {
		const headerWrap = $('.wynn-header');
		const enter = () => { if (wideMQ.matches) clearTimeout(megaTimer); };
		const leave = () => { if (wideMQ.matches) closeMegaSoon(); };
		if (headerWrap) {
			headerWrap.addEventListener('mouseenter', enter);
			headerWrap.addEventListener('mouseleave', leave);
		}
		$$('.wynn-mega').forEach((panel) => {
			panel.addEventListener('mouseenter', enter);
			panel.addEventListener('mouseleave', leave);
		});

		megaTriggers.forEach((trigger) => {
			const key = trigger.dataset.wynnMegaTrigger;
			trigger.addEventListener('mouseenter', () => { if (wideMQ.matches) openMega(key); });
			trigger.addEventListener('focus', () => { if (wideMQ.matches) openMega(key); });
			trigger.addEventListener('click', (e) => {
				if (!wideMQ.matches) return; // narrow: real navigation to href
				const panel = $(`.wynn-mega[data-wynn-mega="${key}"]`);
				if (panel && panel.hidden) {
					e.preventDefault(); // first click opens, second navigates
					openMega(key);
				}
			});
		});

		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') closeMega();
		});
		wideMQ.addEventListener('change', (e) => { if (!e.matches) closeMega(); });
	}

	// Bridal sub-categories toggle inside the collection mega (+/-, aria).
	const bridalToggle = $('[data-wynn-bridal-toggle]');
	if (bridalToggle) {
		bridalToggle.addEventListener('click', () => {
			const subs = $('[data-wynn-bridal-subs]');
			const open = subs.hidden;
			subs.hidden = !open;
			bridalToggle.textContent = open ? '−' : '+';
			bridalToggle.setAttribute('aria-expanded', String(open));
		});
	}

	// =========================================================================
	// Mobile hamburger nav — aria-expanded + scroll lock + focus trap
	// =========================================================================
	const burger = $('[data-wynn-mobile-open]');
	if (burger) {
		burger.addEventListener('click', () => {
			const expanded = burger.getAttribute('aria-expanded') === 'true';
			if (expanded) closeOverlay('mobile-nav');
			else openOverlay('mobile-nav');
		});
	}

	// Accordion groups inside the mobile nav.
	$$('[data-wynn-acc]').forEach((row) => {
		row.addEventListener('click', () => {
			const panel = document.getElementById(row.getAttribute('aria-controls'));
			if (!panel) return;
			const open = panel.hidden;
			// Close any other open accordion first (mockup keeps one open).
			$$('[data-wynn-acc]').forEach((other) => {
				const otherPanel = document.getElementById(other.getAttribute('aria-controls'));
				if (otherPanel && other !== row) {
					otherPanel.hidden = true;
					other.setAttribute('aria-expanded', 'false');
					const sign = $('.wynn-mobile-nav__sign', other);
					if (sign) sign.textContent = '+';
				}
			});
			panel.hidden = !open;
			row.setAttribute('aria-expanded', String(open));
			const sign = $('.wynn-mobile-nav__sign', row);
			if (sign) sign.textContent = open ? '−' : '+';
		});
	});

	// =========================================================================
	// Search overlay — Store API live results, 250ms debounce
	// =========================================================================
	const searchInput = $('[data-wynn-search-input]');
	const searchResults = $('[data-wynn-search-results]');
	const searchLabel = $('[data-wynn-search-label]');

	function storeApiProducts() {
		const base = (window.wcSettings && window.wcSettings.storeApiUrls && window.wcSettings.storeApiUrls.products)
			|| '/wp-json/wc/store/v1/products';
		return base;
	}

	function money(product) {
		const p = product.prices || {};
		const unit = parseInt(p.currency_minor_unit || '2', 10);
		const value = parseInt(p.price || '0', 10) / Math.pow(10, unit);
		const symbol = p.currency_symbol || '$';
		return symbol + value.toLocaleString('en-SG', { minimumFractionDigits: value % 1 ? 2 : 0 });
	}

	function renderResults(products, label) {
		if (!searchResults) return;
		searchResults.innerHTML = '';
		if (searchLabel) searchLabel.textContent = label;
		if (!products.length) {
			searchResults.innerHTML = '<p class="wynn-search__empty">No pieces match that yet — try a collection name, or browse the shop.</p>';
			return;
		}
		products.forEach((product) => {
			const img = (product.images && product.images[0] && product.images[0].src) || '';
			const tile = document.createElement('div');
			tile.className = 'wynn-search__tile';
			tile.innerHTML =
				'<a href="' + product.permalink + '">' +
					'<div class="wynn-search__thumb">' + (img ? '<img src="' + img + '" alt="' + (product.name || '') + '" loading="lazy">' : '') + '</div>' +
					'<div class="wynn-search__name">' + product.name + '</div>' +
					'<div class="wynn-search__price">' + money(product) + '</div>' +
				'</a>';
			searchResults.appendChild(tile);
		});
	}

	let searchDebounce = null;
	function runSearch(term) {
		const url = new URL(storeApiProducts(), window.location.origin);
		url.searchParams.set('per_page', term ? '8' : '4');
		url.searchParams.set('catalog_visibility', 'catalog');
		if (term) url.searchParams.set('search', term);
		fetch(url.toString())
			.then((r) => (r.ok ? r.json() : []))
			.then((products) => {
				renderResults(Array.isArray(products) ? products : [], term ? products.length + (products.length === 1 ? ' piece' : ' pieces') : 'Popular right now');
			})
			.catch(() => {});
	}

	if (searchInput) {
		searchInput.addEventListener('input', () => {
			clearTimeout(searchDebounce);
			const term = searchInput.value.trim();
			searchDebounce = setTimeout(() => runSearch(term), 250);
		});
	}

	// Preload "Popular right now" the first time the overlay opens.
	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-wynn-open="search"]') && searchResults && !searchResults.dataset.loaded) {
			searchResults.dataset.loaded = '1';
			runSearch('');
		}
	});

	// =========================================================================
	// Favourites — localStorage + delegation API for S3 blocks
	// =========================================================================
	function readFavs() {
		try { return JSON.parse(localStorage.getItem(FAVS_KEY)) || []; }
		catch (err) { return []; }
	}

	function writeFavs(favs) {
		localStorage.setItem(FAVS_KEY, JSON.stringify(favs));
	}

	function isFav(id) {
		return readFavs().some((f) => String(f.id) === String(id));
	}

	function syncFavButtons() {
		$$('[data-wynn-fav]').forEach((btn) => {
			btn.classList.toggle('is-saved', isFav(btn.dataset.id));
			btn.setAttribute('aria-pressed', String(isFav(btn.dataset.id)));
		});
		const count = $('[data-wynn-fav-count]');
		if (count) {
			const n = readFavs().length;
			count.textContent = String(n);
			count.hidden = n === 0;
		}
	}

	function renderFavDrawer() {
		const list = $('[data-wynn-favs-list]');
		if (!list) return;
		const favs = readFavs();
		list.innerHTML = '';
		if (!favs.length) {
			list.innerHTML = '<p class="wynn-drawer__empty">Nothing saved yet — tap the heart on any piece.</p>';
			return;
		}
		favs.forEach((f) => {
			const line = document.createElement('div');
			line.className = 'wynn-drawer__line';
			line.innerHTML =
				'<div class="wynn-drawer__thumb">' + (f.img ? '<img src="' + f.img + '" alt="' + (f.name || '') + '">' : '') + '</div>' +
				'<div>' +
					'<div class="wynn-drawer__name"><a href="' + (f.url || '#') + '">' + (f.name || '') + '</a></div>' +
					(f.note ? '<div class="wynn-drawer__note">' + f.note + '</div>' : '') +
					'<div class="wynn-drawer__row">' +
						'<span class="wynn-drawer__price">' + (f.price || '') + '</span>' +
						'<button type="button" class="wynn-drawer__remove" data-wynn-fav-remove="' + f.id + '">Remove</button>' +
					'</div>' +
				'</div>';
			list.appendChild(line);
		});
	}

	// Delegated heart toggle — S3 product/card blocks just render
	// <button data-wynn-fav data-id data-name data-price data-url data-img>.
	document.addEventListener('click', (e) => {
		const btn = e.target.closest('[data-wynn-fav]');
		if (btn) {
			e.preventDefault();
			e.stopPropagation();
			const id = String(btn.dataset.id);
			let favs = readFavs();
			if (isFav(id)) {
				favs = favs.filter((f) => String(f.id) !== id);
			} else {
				favs.push({
					id,
					name: btn.dataset.name || '',
					note: btn.dataset.note || '',
					price: btn.dataset.price || '',
					url: btn.dataset.url || '',
					img: btn.dataset.img || '',
				});
			}
			writeFavs(favs);
			syncFavButtons();
			renderFavDrawer();
			document.dispatchEvent(new CustomEvent('wynn:fav', { detail: { id, saved: isFav(id), favs } }));
			return;
		}
		const remove = e.target.closest('[data-wynn-fav-remove]');
		if (remove) {
			const id = String(remove.dataset.wynnFavRemove);
			writeFavs(readFavs().filter((f) => String(f.id) !== id));
			syncFavButtons();
			renderFavDrawer();
			document.dispatchEvent(new CustomEvent('wynn:fav', { detail: { id, saved: false, favs: readFavs() } }));
		}
	});

	// Refresh drawer contents whenever it opens.
	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-wynn-open="favs"]')) renderFavDrawer();
	});

	syncFavButtons();

	// =========================================================================
	// Shopping bag — delegate to the parent-owned WooCommerce mini-cart
	// =========================================================================
	const bagBtn = $('[data-wynn-bag]');
	const cartCount = $('[data-wynn-cart-count]');

	function syncCartBadge() {
		if (!cartCount) return;
		const badge = $('.wc-block-mini-cart__badge');
		const n = badge ? parseInt(badge.textContent.trim(), 10) || 0 : 0;
		cartCount.textContent = String(n);
		cartCount.hidden = n === 0;
	}

	if (bagBtn) {
		bagBtn.addEventListener('click', () => {
			const wooBtn = $('.wc-block-mini-cart__button');
			if (wooBtn) wooBtn.click();
			else window.location.href = '/cart/';
		});
	}

	document.body.addEventListener('wc-blocks_added_to_cart', () => {
		syncCartBadge();
		toast('Added to your bag');
	});
	document.body.addEventListener('wc_fragments_refreshed', syncCartBadge);

	// The badge updates when the mini-cart drawer mutates — watch it.
	const wooBadge = $('.wc-block-mini-cart__badge');
	if (wooBadge && window.MutationObserver) {
		new MutationObserver(syncCartBadge).observe(wooBadge, { childList: true, characterData: true, subtree: true });
	}
	// Also re-check after load (fragments can render late).
	window.addEventListener('load', syncCartBadge);
	syncCartBadge();

	// =========================================================================
	// Modals — member / size guide
	// =========================================================================
	document.addEventListener('click', (e) => {
		const opener = e.target.closest('[data-wynn-modal-open]');
		if (opener) {
			e.preventDefault();
			closeOverlay('mobile-nav');
			closeMega();
			openOverlay('modal-' + opener.dataset.wynnModalOpen);
		}
	});

	// =========================================================================
	// Footer locale selectors — language + ship-to, persisted, mobile sync
	// =========================================================================
	const localeBackdrop = $('[data-wynn-locale-backdrop]');

	function closeLocalePopups() {
		$$('[data-wynn-locale-popup]').forEach((popup) => { popup.hidden = true; });
		$$('[data-wynn-locale-btn]').forEach((btn) => {
			btn.classList.remove('is-open');
			btn.setAttribute('aria-expanded', 'false');
			const caret = $('.wynn-locale__caret', btn);
			if (caret) caret.textContent = '▼';
		});
		if (localeBackdrop) localeBackdrop.hidden = true;
	}

	function openLocalePopup(which) {
		closeLocalePopups();
		const popup = $(`[data-wynn-locale-popup="${which}"]`);
		const btn = $(`[data-wynn-locale-btn="${which}"]`);
		if (!popup || !btn) return;
		popup.hidden = false;
		btn.classList.add('is-open');
		btn.setAttribute('aria-expanded', 'true');
		const caret = $('.wynn-locale__caret', btn);
		if (caret) caret.textContent = '▲';
		if (localeBackdrop) localeBackdrop.hidden = false;
	}

	$$('[data-wynn-locale-btn]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const which = btn.dataset.wynnLocaleBtn;
			const popup = $(`[data-wynn-locale-popup="${which}"]`);
			if (popup && !popup.hidden) closeLocalePopups();
			else openLocalePopup(which);
		});
	});

	if (localeBackdrop) localeBackdrop.addEventListener('click', closeLocalePopups);

	function applyLang(value, persist = true) {
		const out = $(`[data-wynn-locale-value="lang"]`);
		if (out) out.textContent = value;
		const mobile = $('[data-wynn-mobile-lang]');
		if (mobile) mobile.textContent = value;
		$$('[data-wynn-lang-select]').forEach((opt) => {
			const selected = opt.dataset.wynnLangSelect === value;
			opt.classList.toggle('is-selected', selected);
			const check = $('.wynn-locale__check', opt);
			if (check) check.textContent = selected ? '✓' : '';
		});
		if (persist) localStorage.setItem(LANG_KEY, value);
	}

	function applyCur(value, dot, persist = true) {
		const out = $(`[data-wynn-locale-value="ship"]`);
		if (out) out.textContent = value;
		const mobile = $('[data-wynn-mobile-cur]');
		if (mobile) mobile.textContent = value;
		const dotEl = $('[data-wynn-locale-dot]');
		if (dotEl && dot) dotEl.style.background = dot;
		$$('[data-wynn-cur-select]').forEach((opt) => {
			const selected = opt.dataset.wynnCurSelect === value;
			opt.classList.toggle('is-selected', selected);
			const check = $('.wynn-locale__check', opt);
			if (check) check.textContent = selected ? '✓' : '';
		});
		if (persist) {
			localStorage.setItem(CUR_KEY, value);
			if (dot) localStorage.setItem(CUR_DOT_KEY, dot);
		}
	}

	document.addEventListener('click', (e) => {
		const langOpt = e.target.closest('[data-wynn-lang-select]');
		if (langOpt) {
			applyLang(langOpt.dataset.wynnLangSelect);
			closeLocalePopups();
			return;
		}
		const curOpt = e.target.closest('[data-wynn-cur-select]');
		if (curOpt) {
			applyCur(curOpt.dataset.wynnCurSelect, curOpt.dataset.dot);
			closeLocalePopups();
			// Currency conversion is parent-owned commerce; S5 wires real rates.
			toast('Shipping to ' + curOpt.dataset.wynnCurSelect.split(' · ')[0]);
		}
	});

	// Restore persisted locale choices.
	try {
		const savedLang = localStorage.getItem(LANG_KEY);
		if (savedLang) applyLang(savedLang, false);
		const savedCur = localStorage.getItem(CUR_KEY);
		if (savedCur) applyCur(savedCur, localStorage.getItem(CUR_DOT_KEY), false);
	} catch (err) { /* private mode */ }

	// =========================================================================
	// Newsletter — interim toast until S5 wires the real list endpoint
	// =========================================================================
	$$('[data-wynn-newsletter]').forEach((form) => {
		form.addEventListener('submit', (e) => {
			e.preventDefault();
			const email = $('input[type="email"]', form);
			if (email && email.checkValidity() && email.value) {
				toast('Thank you — you are on the list');
				email.value = '';
			} else if (email) {
				email.reportValidity();
			}
		});
	});

	// Public API for S3 blocks (e.g. sync hearts after AJAX renders).
	window.wynnChrome = { openOverlay, closeOverlay, closeAllOverlays, toast, syncFavButtons, isFav, readFavs };
})();
